import { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';

export interface GameContainerProps {
  /** Phaser game configuration */
  config: Phaser.Types.Core.GameConfig;
  /** Called when the game is ready */
  onGameReady?: (game: Phaser.Game) => void;
  /** Called when the game is destroyed */
  onGameDestroy?: () => void;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * React component that hosts a Phaser game canvas.
 * Manages game lifecycle and cleanup.
 */
export function GameContainer({
  config,
  onGameReady,
  onGameDestroy,
  className = '',
}: GameContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Prevent double initialization in React Strict Mode
    if (gameRef.current) return;

    try {
      // Create game with container reference
      const gameConfig: Phaser.Types.Core.GameConfig = {
        ...config,
        parent: containerRef.current,
        callbacks: {
          ...config.callbacks,
          postBoot: (game) => {
            setIsLoading(false);
            onGameReady?.(game);
            config.callbacks?.postBoot?.(game);
          },
        },
      };

      gameRef.current = new Phaser.Game(gameConfig);
    } catch (err) {
      console.error('Failed to initialize Phaser game:', err);
      setError('Failed to load game');
      setIsLoading(false);
    }

    // Cleanup on unmount
    return () => {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
        onGameDestroy?.();
      }
    };
  }, [config, onGameReady, onGameDestroy]);

  return (
    <div className={`relative ${className}`}>
      {/* Game canvas container */}
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-hidden"
        style={{ touchAction: 'none' }} // Prevent browser touch gestures
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Loading game...</p>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <p className="text-red-400 mb-2">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-purple-400 hover:text-purple-300 text-sm"
            >
              Reload page
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Create a basic Phaser game configuration
 */
// eslint-disable-next-line react-refresh/only-export-components
export function createGameConfig(
  scenes: Phaser.Types.Scenes.SceneType[],
  options: Partial<Phaser.Types.Core.GameConfig> = {}
): Phaser.Types.Core.GameConfig {
  const { scale: scaleOverrides, physics: physicsOverrides, ...rest } = options;
  const width = (rest.width as number) || 800;
  const height = (rest.height as number) || 600;

  return {
    type: Phaser.AUTO,
    width,
    height,
    backgroundColor: '#1e293b',
    scene: scenes,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      expandParent: false,
      width,
      height,
      max: { width, height },
      ...scaleOverrides,
    },
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 0 },
        debug: import.meta.env.DEV,
      },
      ...physicsOverrides,
    },
    input: {
      keyboard: true,
      mouse: true,
      touch: true,
    },
    render: {
      pixelArt: true,
      antialias: false,
    },
    ...rest,
  };
}
